import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Container from "./Container";
import { InfoCircle } from "../assets/img";
import FAQSection from "./FAQSection";
import DotSpinner from "./DotSpinnter";
import { Contract, formatUnits } from "ethers";
import { TEAM_LIST } from "../web3/teams";
import { CONTRACTS } from "../web3/contracts";
import { getSepoliaReadProvider } from "../web3/readProvider";
import { SPORTFOLIO_IPO_ABI } from "../web3/abis";
import { useWallet } from "../web3/useWallet";
import { toast } from "react-toastify";

const STATIC_PLAYER_SCORE = 15.84;
const STATIC_PLAYER_SCORE_CHANGE = "+2.739%";

const OnBoarding = () => {
  const teamDropdownRef = useRef(null);
  const wallet = useWallet();
  const [isTeamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(TEAM_LIST[0]);
  const [quantity, setQuantity] = useState(101);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUnitPrice, setCurrentUnitPrice] = useState(null);
  const [remainingTokens, setRemainingTokens] = useState(null);
  const [tokenCost, setTokenCost] = useState(null);
  const [platformFee, setPlatformFee] = useState(null);
  const [ownershipPct, setOwnershipPct] = useState("");
  const [error, setError] = useState("");
  const [isQuoting, setIsQuoting] = useState(false);

  const MAX_QUOTE_QTY = 10000;
  const MAX_BUY_QTY = 10000;

  const totalRequired = useMemo(() => {
    if (tokenCost == null || platformFee == null) return null;
    return tokenCost + platformFee;
  }, [platformFee, tokenCost]);

  const isSepolia = wallet?.isSepolia;
  const canClickBuy =
    !isLoading &&
    Number.isFinite(quantity) &&
    quantity > 0 &&
    (remainingTokens == null || quantity <= remainingTokens);

  const formattedUnitPrice =
    currentUnitPrice == null ? "-" : `$${Number(formatUnits(currentUnitPrice, 6)).toFixed(2)}`;

  const formattedTotalRequired =
    totalRequired == null ? "-" : `$${Number(formatUnits(totalRequired, 6)).toFixed(2)}`;

  const playerCards = useMemo(() => {
    const names = selectedTeam?.players?.length ? selectedTeam.players : [];
    if (!names.length) return [];
    return names.map((name, idx) => ({
      name,
      value: STATIC_PLAYER_SCORE,
      change: STATIC_PLAYER_SCORE_CHANGE,
      image: selectedTeam.avatars?.[idx] ?? null,
    }));
  }, [selectedTeam]);

  const refreshOnChain = useCallback(
    async ({ team = selectedTeam, qty = quantity, addr = wallet?.account } = {}) => {
      try {
        setError("");
        const readProvider = await getSepoliaReadProvider();
        const ipoRead = new Contract(
          CONTRACTS.sportfolioIPO,
          SPORTFOLIO_IPO_ABI,
          readProvider
        );

        if (qty > MAX_QUOTE_QTY) {
          setTokenCost(null);
          setPlatformFee(null);
          const msg = `Quantity too large to quote quickly. Please enter ${MAX_QUOTE_QTY.toLocaleString()} or less.`;
          setError(msg);
          toast.error(msg);
          return;
        }

        const quote = await Promise.all([
          ipoRead.getCurrentPrice(team.tokenId),
          ipoRead.getRemainingTokens(team.tokenId),
          qty > 0
            ? ipoRead.calculatePurchaseCost(team.tokenId, qty)
            : Promise.resolve([null, null]),
        ]);
        const unitPrice = quote[0];
        const remaining = quote[1];
        const costResult = quote[2];

        setCurrentUnitPrice(unitPrice);
        setRemainingTokens(Number(remaining));

        if (qty > 0) {
          setTokenCost(costResult?.tokenCost ?? costResult?.[0] ?? null);
          setPlatformFee(costResult?.platformFee ?? costResult?.[1] ?? null);
        } else {
          setTokenCost(null);
          setPlatformFee(null);
        }

        if (!addr) {
          setOwnershipPct("");
          return;
        }

        const [bal, supply] = await Promise.all([
          ipoRead.balanceOf(addr, team.tokenId),
          ipoRead.getTotalSupply(team.tokenId),
        ]);

        const supplyNum = Number(supply);
        const balNum = Number(bal);
        if (supplyNum > 0) {
          const pct = (balNum / supplyNum) * 100;
          setOwnershipPct(pct > 0 ? `${pct.toFixed(6)}%` : "0%");
        } else {
          setOwnershipPct("-");
        }
      } catch (e) {
        const raw = e?.shortMessage || e?.message || "";
        const isNetwork =
          /failed to fetch|network error|fetch/i.test(raw) || e?.code === "NETWORK_ERROR";
        const msg = isNetwork
          ? "Could not load on-chain data. If MetaMask is installed, switch to Sepolia (reads use your wallet and avoid browser RPC limits). You can also set REACT_APP_SEPOLIA_RPC_URL to an Infura/Alchemy Sepolia URL allowed for your domain."
          : raw || "Failed to read from chain.";
        setError(msg);
      } finally {
        setIsQuoting(false);
      }
    },
    [MAX_QUOTE_QTY, quantity, selectedTeam, wallet?.account]
  );

  const handleBuy = async () => {
    try {
      setError("");
      if (!wallet?.account) {
        if (!wallet?.ethereumPresent) {
          toast.error("MetaMask is not installed. Please install it to connect your wallet.");
          return;
        }
        wallet?.connect?.();
        return;
      }
      if (!wallet?.isSepolia) {
        const msg = "Please switch to Sepolia network.";
        setError(msg);
        toast.error(msg);
        return;
      }
      if (!totalRequired) {
        const msg = "Unable to quote cost. Please try again.";
        setError(msg);
        toast.error(msg);
        return;
      }
      if (quantity > MAX_BUY_QTY) {
        const msg = `Quantity too large for a single purchase. Please enter ${MAX_BUY_QTY.toLocaleString()} or less.`;
        setError(msg);
        toast.error(msg);
        return;
      }

      setIsLoading(true);
      const provider = wallet?.getProvider?.();
      if (!provider) {
        const msg = "MetaMask not found.";
        setError(msg);
        toast.error(msg);
        return;
      }
      const signer = await provider.getSigner();
      const usdc = new Contract(CONTRACTS.usdc, ["function approve(address,uint256) returns (bool)","function allowance(address,address) view returns (uint256)"], signer);
      const ipo = new Contract(CONTRACTS.sportfolioIPO, SPORTFOLIO_IPO_ABI, signer);

      const allowance = await usdc.allowance(wallet.account, CONTRACTS.sportfolioIPO);
      if (allowance < totalRequired) {
        const approveTx = await usdc.approve(CONTRACTS.sportfolioIPO, totalRequired);
        await approveTx.wait();
      }
      const buyTx = await ipo.buyTokens(selectedTeam.tokenId, quantity);
      await buyTx.wait();
      await wallet?.refreshUsdcBalance?.(wallet.account);
      await wallet?.refreshTeamHoldings?.(wallet.account);
      await refreshOnChain({ team: selectedTeam, qty: quantity, addr: wallet.account });
      toast.success("Purchase successful.");
    } catch (e) {
      const msg =
        e?.shortMessage ||
        e?.message ||
        "Transaction failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target)
      ) {
        setTeamDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsQuoting(true);
    const t = setTimeout(() => {
      refreshOnChain({ team: selectedTeam, qty: quantity, addr: wallet?.account });
    }, 250);
    return () => clearTimeout(t);
  }, [wallet?.account, selectedTeam, quantity, refreshOnChain]);

  return (
    <>
      <Container className="mb-10 mt-10">
        <div className="font-poppins">
          <div className="flex items-center justify-between">
            <h1 className="text-base sm:text-3xl md:text-4xl font-bold">
              Order Details
            </h1>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <div
                className="bg-secondary rounded shadow-md flex items-center py-1.5 px-3 md:px-5"
                ref={teamDropdownRef}
              >
                <div className="relative w-full">
                  {/* Dropdown Trigger */}
                  <div
                    className="flex items-center gap-2 md:gap-6 justify-between cursor-pointer"
                    onClick={() => setTeamDropdownOpen((prev) => !prev)}
                  >
                    <div className="flex items-center gap-2">
                      {selectedTeam?.logo && (
                        <div className="w-4 h-4 md:w-8 md:h-8 flex items-center rounded-full overflow-hidden">
                          <img src={selectedTeam.logo} alt="" />
                        </div>
                      )}
                      <strong className="font-medium text-white text-xs md:text-sm font-roboto">
                        {selectedTeam.name}
                      </strong>
                    </div>
                    <i className="fa fa-angle-down text-white text-xl md:text-2xl"></i>
                  </div>

                  {/* Dropdown Menu */}
                  {isTeamDropdownOpen && (
                    <div className="absolute z-50 -left-10 md:-left-12 w-36 md:w-48 bg-[#ebeef1] top-[calc(100%+16px)] rounded shadow">
                      {TEAM_LIST.map((team) => {
                        const isSelected = selectedTeam === team;
                        return (
                          <div
                            key={team.name}
                            onClick={() => {
                              setSelectedTeam(team);
                              setTeamDropdownOpen(false);
                            }}
                            className="p-3 cursor-pointer flex items-center hover:bg-gray-200 rounded"
                          >
                            <div className="w-8 h-8 rounded-full overflow-hidden">
                              <img src={team.logo} alt={`${team.name} logo`} />
                            </div>
                            <span
                              className={`ml-3 uppercase ${
                                isSelected ? "font-bold" : "font-normal"
                              }`}
                            >
                              {team.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {error && (
            <div className="mt-3 text-sm bg-red-50 text-red-800 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
          {!isSepolia && wallet?.account && (
            <div className="mt-3 text-sm bg-yellow-50 text-yellow-900 border border-yellow-200 rounded px-3 py-2">
              Please switch MetaMask to Sepolia.
            </div>
          )}
          <div className="grid grid-cols-12 mt-6 gap-0">
            {/* Left - Images */}
            <div className="col-span-12 lg:col-span-6 md:min-h-[584px] md:h-full">
              <div
                className="lg:rounded-l-xl px-6 py-8 relative w-full h-full"
                style={{ backgroundColor: selectedTeam.bgColor }}
              >
                {/* Vertical Title */}
                <div
                  className="absolute left-6 xmd:left-8 md:left-10 1xl:left-8 bottom-16 xs:bottom-20 sm:bottom-1/4 lg:bottom-1/4 1xl:bottom-28 translate-y-full -rotate-90 origin-left font-bold text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-xl 1xl:text-4xl w-auto"
                  style={{ color: selectedTeam.accentColor }}
                >
                  Player Importance Score
                </div>

                {/* Grid */}
                <div className="grid grid-cols-3 gap-x-3 gap-y-4 ml-4 sm:ml-6 md:ml-10 h-full">
                  {playerCards.map((player, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center justify-start text-center"
                    >
                      {/* Player Image */}
                      <div
                        className="w-14 h-14 xs:w-16 xs:h-16 sm:w-24 sm:h-24 xmd:w-28 xmd:h-28 md:w-40 md:h-40 lg:w-24 lg:h-24 1xl:w-32 1xl:h-32 rounded-full overflow-hidden shadow-custom flex-shrink-0 relative"
                        style={{ border: `3px solid ${selectedTeam.accentColor}` }}
                      >
                        {/* shimmer placeholder */}
                        <div className="absolute inset-0 animate-pulse bg-white/20" />
                        {player.image && (
                          <img
                            src={player.image}
                            alt=""
                            className="w-full h-full object-cover absolute inset-0"
                            style={{ opacity: 0, transition: "opacity 0.5s ease" }}
                            onLoad={(e) => { e.currentTarget.style.opacity = 1; }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                      </div>

                      {/* Player Name */}
                      <p className="mt-1 text-[9px] xs:text-[10px] sm:text-sm md:text-xl lg:text-sm 1xl:text-base font-medium text-white font-poppins truncate w-full">
                        {player.name}
                      </p>

                      {/* Stats */}
                      <p className="text-[7px] xs:text-[8px] sm:text-[10px] md:text-sm lg:text-[10px] font-medium text-white">
                        {player.value} {" • "}
                        <span className="text-[#009846]">{player.change}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Cards Section */}
            <div className="flex flex-col justify-start col-span-12 lg:col-span-6 gap-4 py-8 px-4 md:px-6 bg-secondary lg:rounded-r-xl">
              <div className="flex flex-col sm:flex-row items-start  sm:items-center sm:justify-between gap-4">
                <p className="font-bold text-base md:text-lg lg:text-xl text-white">
                  Team Ticker :{" "}
                  <span className="text-yellow-400 font-semibold">
                    {" "}
                    {selectedTeam?.name}{" "}
                  </span>
                </p>
                {/* <div className="flex items-center justify-end gap-2">
                  <img
                    width={24}
                    height={24}
                    alt="Boston Celtics"
                    src={BostonCeltics}
                  />
                  <p className="text-sm font-medium text-white">
                    Boston Celtics
                  </p>
                </div> */}
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-6 gap-x-2 gap-y-6 overflow-hidden">
                {/* Current Market Price */}
                <div className="col-span-6 sm:col-span-3 bg-lighterSecondary text-white rounded-md shadow-md p-4 md:h-[185px] relative">
                  <div className="flex items-center justify-start gap-2">
                    <p className="text-sm mt-4 sm:mt-1 md:mt-4">
                      Current Market Price (Team)
                    </p>

                    {/* Icon + Tooltip wrapper */}
                    <div className="relative flex-shrink-0 group mt-4 sm:mt-1 md:mt-4">
                      <InfoCircle className="w-5 h-5 text-gray-500 cursor-pointer" />

                      {/* Tooltip */}
                      <div
                        className="absolute left-1/2 -translate-x-full sm:-translate-x-1/2 top-full mt-2 
                          hidden group-hover:flex flex-col items-center z-10"
                      >
                        {/* Arrow */}
                        <div className="w-0 h-0 sm:border-l-8 sm:border-r-8 sm:border-b-[12px] border-transparent border-b-gray-800"></div>

                        {/* Tooltip Box */}
                        <div className="bg-gray-800 text-white text-xs rounded-lg p-2 w-52 sm:w-64 shadow-lg">
                          Current Market Price of the team = Market Sentiment +
                          Player Importance Score Total + Team&apos;s Current
                          Valuation
                        </div>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-2xl md:text-3xl lg:text-4xl 1xl:text-5xl mt-4">
                    {formattedUnitPrice}
                  </h2>
                  <p className="text-[#0E983D] font-medium text-sm mt-4">
                    +2.7912%
                  </p>
                </div>

                {/* Enter Quantity */}
                <div className="col-span-6 sm:col-span-3 bg-lighterSecondary text-white rounded-md shadow-md p-4 border-l-[3px] border-primary md:h-[185px]">
                  <div className="flex items-center justify-start gap-2 mt-4">
                    <p className="text-sm font-medium">Enter Quantity</p>
                    <div className="relative flex-shrink-0 group">
                      <InfoCircle className="w-5 h-5 text-gray-500 cursor-pointer" />
                      <div className="absolute left-1/2 -translate-x-full sm:-translate-x-1/2 top-full mt-2 hidden group-hover:flex flex-col items-center z-10">
                        <div className="w-0 h-0 sm:border-l-8 sm:border-r-8 sm:border-b-[12px] border-transparent border-b-gray-800"></div>
                        <div className="bg-gray-800 text-white text-xs rounded-lg p-2 w-56 sm:w-72 shadow-lg">
                          Retail cap is 50,000 tokens per team. Exact pricing is computed per token on-chain. Large quantities may take longer to quote, and in some cases may require splitting into multiple transactions (up to 10,000 per transaction recommended).
                        </div>
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    name="quantity"
                    value={quantity}
                    autoFocus
                    min={1}
                    max={remainingTokens ?? undefined}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full border-b border-white/30 bg-transparent focus:outline-none text-2xl md:text-3xl lg:text-4xl 1xl:text-5xl mt-4"
                  />
                  {remainingTokens != null && (
                    <p className="text-xs text-white/70 mt-2">
                      Available: {remainingTokens.toLocaleString()}
                    </p>
                  )}
                </div>

                {/* Approximate Investment */}
                <div className="col-span-6 sm:col-span-3 bg-lighterSecondary text-white rounded-md shadow-md p-4 md:h-[185px]">
                  <p className="text-sm font-medium mt-4">
                    Approximate Investment Amount
                  </p>
                  <div className="flex items-center gap-2">
                    <h2
                      className="text-2xl md:text-3xl lg:text-4xl 1xl:5xl mt-4 sm:mt-1 md:mt-4 truncate"
                      title={formattedTotalRequired}
                    >
                      {formattedTotalRequired}
                    </h2>
                    {isQuoting && (
                      <DotSpinner className="w-[18px] h-[18px]" />
                    )}
                  </div>
                </div>

                {/* Lakers Ownership */}
                <div className="col-span-6 sm:col-span-3 bg-lighterSecondary text-white rounded-md shadow-md p-4 md:h-[185px]">
                  <p className="text-sm font-medium mt-4">
                    Your Team Ownership
                  </p>
                  <div className="flex items-center gap-2">
                    {wallet?.account ? (
                      <>
                        <h2
                          className="text-2xl md:text-3xl lg:text-4xl 1xl:text-5xl mt-4"
                          title={ownershipPct}
                        >
                          {ownershipPct || "—"}
                        </h2>
                        {isLoading && (
                          <DotSpinner className="w-[18px] h-[18px]" />
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          if (!wallet?.ethereumPresent) {
                            toast.error(
                              "MetaMask is not installed. Please install it to connect your wallet."
                            );
                            return;
                          }
                          wallet?.connect?.();
                        }}
                        className="mt-4 bg-primary text-white text-sm font-roboto rounded px-3 py-2 shadow-md"
                        disabled={wallet?.isConnecting}
                      >
                        {wallet?.isConnecting ? "Connecting..." : "Connect to view"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Buy / Sell Buttons */}
                <button
                  className="bg-[#0E983D] h-[50px] rounded py-3 px-[10px] text-base text-white font-roboto col-span-6 sm:col-span-3"
                  onClick={handleBuy}
                  disabled={!canClickBuy}
                >
                  {isLoading ? "Processing..." : "Buy"}
                </button>
                <button disabled className="bg-lighterSecondary h-[50px] rounded py-3 px-[10px] text-base text-white font-roboto col-span-6 sm:col-span-3 cursor-not-allowed">
                  Sell
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="my-12">
          <FAQSection />
        </div>
      </Container>
    </>
  );
};

export default OnBoarding;
